import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Grid,
  Box
} from '@mui/material';
import apiClient from "../../request/http-common";
import { useMutation, useQuery } from "react-query";

const EditInvestment = ({ open, handleClose, investment, onSuccess }) => {
  const [formData, setFormData] = useState({
    investment_plan_id: '',
    amount_invested: '',
    duration: '',
    payment_source: '',
    status: '',
    earning: ''
  });

  const { data: investmentPlans } = useQuery('investment-plans-all', async () => {
    const response = await apiClient.get('/api/investment-plans');
    return response.data.plans;
  });

  useEffect(() => {
    if (investment) {
      setFormData({
        investment_plan_id: investment.investment_plan_id || '',
        amount_invested: investment.amount_invested || '',
        duration: investment.duration || '',
        payment_source: investment.payment_source || '',
        status: investment.status || 'pending',
        earning: investment.earning || '0'
      });
    }
  }, [investment]);

  const { mutate: updateInvestment, isLoading } = useMutation(
    async () => {
      return await apiClient.post(`/api/admin/update-investment/${investment.id}`, formData);
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          localStorage.setItem('notification', JSON.stringify({
            message: res.data.message,
            type: 'success'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          handleClose();
          if (onSuccess) onSuccess();
        }
      },
      onError: (err) => {
        const errorMessage = err.response?.data.message || 'An error occurred';
        localStorage.setItem('notification', JSON.stringify({
          message: errorMessage,
          type: 'error'
        }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const paymentSources = [
    'Funding Wallet',
    'Withdrawable Balance',
    'Referral Wallet',
    'Profit'
  ];

  const statuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'declined', label: 'Declined' },
    { value: 'completed', label: 'Completed' }
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Investment</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Investment Plan</InputLabel>
                <Select
                  name="investment_plan_id"
                  value={formData.investment_plan_id}
                  label="Investment Plan"
                  onChange={handleChange}
                  required
                >
                  {investmentPlans?.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount Invested"
                name="amount_invested"
                type="number"
                value={formData.amount_invested}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Current Earning"
                name="earning"
                type="number"
                value={formData.earning}
                onChange={handleChange}
                helperText="Current profit earned"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Duration"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                helperText="e.g., 30 Days, 60 Days"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Source</InputLabel>
                <Select
                  name="payment_source"
                  value={formData.payment_source}
                  label="Payment Source"
                  onChange={handleChange}
                  required
                >
                  {paymentSources.map((source) => (
                    <MenuItem key={source} value={source}>
                      {source}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleChange}
                  required
                >
                  {statuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={updateInvestment} 
          variant="contained" 
          disabled={isLoading}
        >
          {isLoading ? 'Updating...' : 'Update Investment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInvestment;

